import React, { useState, useEffect, useCallback } from 'react';
import SettingsService from '../../services/SettingsService';

const CREDENTIAL_FIELDS = [
  {
    group: 'Testpress API',
    icon: 'bi-cloud',
    fields: [
      { key: 'credentials.apiBaseUrl', label: 'API Base URL', envVar: 'REQUEST_URL', type: 'text' },
      { key: 'credentials.apiToken', label: 'API Token', envVar: 'API_TOKEN', type: 'password' },
    ],
  },
  {
    group: 'Webhook Keys',
    icon: 'bi-link-45deg',
    fields: [
      { key: 'credentials.webhookPrivateKey', label: 'Private Key', envVar: 'TESTPRESS_WEBHOOK_PRIVATE_KEY', type: 'password' },
      { key: 'credentials.webhookPublicKey', label: 'Public Key', envVar: 'TESTPRESS_WEBHOOK_PUBLIC_KEY', type: 'password' },
    ],
  },
];

const IntegrationsSection = () => {
  const [credentials, setCredentials] = useState({});
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [visibleFields, setVisibleFields] = useState({});

  const fetchCredentials = useCallback(async () => {
    try {
      const data = await SettingsService.getCredentials();
      setCredentials(data.credentials || {});
      setEncryptionEnabled(data.encryptionEnabled);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const handleSave = async (key) => {
    const value = editValues[key];
    if (!value || !value.trim()) return;

    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const data = await SettingsService.updateCredential(key, value.trim());
      setCredentials(prev => ({ ...prev, [key]: data.credential }));
      setEditValues(prev => ({ ...prev, [key]: '' }));
    } catch (err) {
      console.error('Failed to save credential:', err);
    }
    setSaving(prev => ({ ...prev, [key]: false }));
  };

  const handleRevert = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const data = await SettingsService.deleteCredential(key);
      setCredentials(prev => ({ ...prev, [key]: data.credential }));
      setEditValues(prev => ({ ...prev, [key]: '' }));
    } catch (err) {
      console.error('Failed to revert credential:', err);
    }
    setSaving(prev => ({ ...prev, [key]: false }));
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await SettingsService.testConnection();
      setTestResult(data);
    } catch {
      setTestResult({ success: false, message: 'Request failed' });
    }
    setTesting(false);
  };

  const toggleVisibility = (key) => {
    setVisibleFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return <div className="settings-loading"><i className="bi bi-arrow-repeat spin" /> Loading credentials...</div>;
  }

  if (!encryptionEnabled) {
    return (
      <div className="settings-card">
        <div className="settings-card-header"><i className="bi bi-info-circle" /> Encryption Not Configured</div>
        <div style={{ padding: '16px', lineHeight: '1.6' }}>
          <p>Database-backed credential management requires <code>CREDENTIAL_ENCRYPTION_KEY</code> to be set in the server environment.</p>
          <p style={{ marginTop: '8px' }}>Generate a key:</p>
          <code style={{ display: 'block', padding: '8px', background: 'var(--bg-tertiary, #1a1a2e)', borderRadius: '4px', marginTop: '4px' }}>
            node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"
          </code>
          <p style={{ marginTop: '12px' }}>Add it to your <code>.env</code> file and restart the server. Credentials will continue to be read from <code>.env</code> until then.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {CREDENTIAL_FIELDS.map(group => (
        <div key={group.group} className="settings-card">
          <div className="settings-card-header">
            <i className={`bi ${group.icon}`} />
            {group.group}
          </div>

          {group.fields.map(field => {
            const status = credentials[field.key] || { source: 'none', maskedValue: '', configured: false };
            const isEditing = !!editValues[field.key];
            const isSaving = saving[field.key];

            return (
              <div key={field.key} className="settings-row" style={{ flexWrap: 'wrap', gap: '8px' }}>
                <div className="settings-row-info">
                  <span className="settings-row-label">{field.label}</span>
                  <span className="settings-row-help">
                    {status.source === 'database' && <span className="settings-indicator settings-indicator--blue" style={{ fontSize: '11px', padding: '2px 6px' }}>Database</span>}
                    {status.source === 'env' && <span className="settings-indicator settings-indicator--green" style={{ fontSize: '11px', padding: '2px 6px' }}>Environment</span>}
                    {status.source === 'none' && <span className="settings-indicator settings-indicator--red" style={{ fontSize: '11px', padding: '2px 6px' }}>Not configured</span>}
                    {status.configured && (
                      <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontSize: '12px' }}>
                        {visibleFields[field.key] ? status.maskedValue : '********'}
                        <button
                          onClick={() => toggleVisibility(field.key)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', marginLeft: '4px', fontSize: '12px' }}
                          title={visibleFields[field.key] ? 'Hide' : 'Show masked value'}
                        >
                          <i className={`bi ${visibleFields[field.key] ? 'bi-eye-slash' : 'bi-eye'}`} />
                        </button>
                      </span>
                    )}
                  </span>
                </div>
                <div className="settings-row-control" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type={field.type}
                    className="settings-number-input"
                    style={{ minWidth: '200px', flex: 1 }}
                    placeholder={status.configured ? 'Enter new value to update...' : 'Enter value...'}
                    value={editValues[field.key] || ''}
                    onChange={e => setEditValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                    disabled={isSaving}
                  />
                  <button
                    className="data-subnav-pill"
                    onClick={() => handleSave(field.key)}
                    disabled={!isEditing || isSaving}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {isSaving ? <><i className="bi bi-arrow-repeat spin" /> Saving...</> : <><i className="bi bi-check-lg" /> Save</>}
                  </button>
                  {status.source === 'database' && (
                    <button
                      className="data-subnav-pill"
                      onClick={() => handleRevert(field.key)}
                      disabled={isSaving}
                      style={{ whiteSpace: 'nowrap' }}
                      title="Remove DB value and revert to .env"
                    >
                      <i className="bi bi-arrow-counterclockwise" /> Revert
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {group.group === 'Testpress API' && (
            <div className="settings-row">
              <div className="settings-row-info">
                <span className="settings-row-label">Connection Test</span>
                <span className="settings-row-help">Verify the API token works by making a test request to Testpress</span>
              </div>
              <div className="settings-row-control" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="data-subnav-pill active"
                  onClick={handleTestConnection}
                  disabled={testing}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {testing ? <><i className="bi bi-arrow-repeat spin" /> Testing...</> : <><i className="bi bi-plug" /> Test Connection</>}
                </button>
                {testResult && (
                  <span className={`settings-indicator settings-indicator--${testResult.success ? 'green' : 'red'}`}>
                    <span className="settings-indicator-dot" />
                    {testResult.message}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default IntegrationsSection;
